import { ConfigManager } from "@/application/config/Config";
import { LoggerFactory } from "@/application/logging/Logger";
import {
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CheckoutSessionResponse,
  CancelSubscriptionRequest,
  SaveSubscriptionRequest,
  StripePlansResponse,
} from "@/types";

export class StripeRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();
  private readonly stripeBaseUrl = this.config.stripeBaseUrl || this.config.baseUrl;

  private get jsonHeaders(): HeadersInit {
    return {
      accept: "*/*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
    };
  }

  async createCheckoutSession(
    payload: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse | null> {
    const url = `${this.stripeBaseUrl}/create_checkout_session`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.jsonHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      this.logger.error("createCheckoutSession failed", error as Error, { ...payload });
      return null;
    }
  }

  async getCheckoutSession(sessionId: string): Promise<CheckoutSessionResponse | null> {
    const url = `${this.stripeBaseUrl}/checkout_session?session_id=${encodeURIComponent(sessionId)}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      this.logger.error("getCheckoutSession failed", error as Error, { sessionId });
      return null;
    }
  }

  async cancelSubscription(payload: CancelSubscriptionRequest): Promise<boolean> {
    const url = `${this.stripeBaseUrl}/cancel_subscription`;

    try {
      const body = new URLSearchParams();
      body.set("subscription_id", String(payload.subscription_id));
      body.set("cancel_at_period_end", String(payload.cancel_at_period_end));

      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "*/*",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: body.toString(),
      });

      return response.ok;
    } catch (error) {
      this.logger.error("cancelSubscription failed", error as Error, { ...payload });
      return false;
    }
  }

  async saveSubscription(payload: SaveSubscriptionRequest): Promise<boolean> {
    const url = `${this.stripeBaseUrl}/save_subscription`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.jsonHeaders,
        body: JSON.stringify(payload),
      });

      return response.ok;
    } catch (error) {
      this.logger.error("saveSubscription failed", error as Error, { ...payload });
      return false;
    }
  }

  async getCurrentSubscription(userId: string): Promise<any | null> {
    const url = `${this.stripeBaseUrl}/current_subscription?user_id=${encodeURIComponent(userId)}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      this.logger.error("getCurrentSubscription failed", error as Error, { userId });
      return null;
    }
  }

  async getPlans(): Promise<StripePlansResponse | null> {
    const url = `${this.stripeBaseUrl}/plans`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      this.logger.error("getPlans failed", error as Error);
      return null;
    }
  }
}
